import { Badge } from "@/components/ui/badge";
import { Card, CardTitle } from "@/components/ui/card";
import useReconEnabled from "@/hooks/use-recon-enabled";
import { InvoiceStatus, InvoiceType } from "@/lib/constants/invoiceStatus";
import { cn } from "@/lib/utils";
import { useGetAllInvoices } from "@/queries/invoices/get-all";
import { useInvoiceLiveCountsSubscription } from "@/queries/invoices/live-counts/live-counts";
import { useTransactionsLiveCountsSubscription } from "@/queries/transactions/live-counts/live-counts";
import { Check, Loader2 } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "usehooks-ts";

type AiProcessingCardProps = {
  screen: "invoices" | "txn-todos";
  className?: string;
};

export default function AIProcessingCard({
  screen,
  className,
}: AiProcessingCardProps) {
  const [lastSeen, setLastSeen] = useLocalStorage<number | null>(
    "lastSeenAIProcessingCard",
    null,
  );
  const [lastSeenPrevious, setLastSeenPrevious] = useLocalStorage<
    number | null
  >("lastSeenAIProcessingCardPrevious", null);
  const { reconEnabled, loading: reconEnabledLoading } = useReconEnabled();
  const { t } = useTranslation();

  const invoiceQuery = useMemo(
    () => ({
      status: {
        value: InvoiceStatus.NEW,
        op: "eq" as const,
      },
      createdAt: {
        value: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
        op: "gt" as const,
      },
    }),
    [],
  );

  const {
    liveCounts: liveCountsInvoiceProcessing,
    loading: liveCountsInvoiceProcessingLoading,
  } = useInvoiceLiveCountsSubscription({
    query: invoiceQuery,
  });

  const invoiceReconQuery = useMemo(
    () => ({
      reconTriggered: {
        value: false,
        op: "eq" as const,
      },
      createdAt: {
        value: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
        op: "gt" as const,
      },
    }),
    [],
  );

  const {
    liveCounts: liveCountsInvoiceRecon,
    loading: liveCountsInvoiceReconLoading,
  } = useInvoiceLiveCountsSubscription({
    query: invoiceReconQuery,
  });

  const txnReconQuery = useMemo(
    () => ({
      reconTriggered: {
        value: false,
        op: "eq" as const,
      },
      createdAt: {
        value: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
        op: "gt" as const,
      },
      side: {
        value: "debit",
        op: "eq" as const,
      },
    }),
    [],
  );

  const { liveCounts: liveCountsTxnRecon, loading: liveCountsTxnReconLoading } =
    useTransactionsLiveCountsSubscription({
      query: txnReconQuery,
    });

  const lastSeenQuery = useMemo(
    () => ({
      createdAt: {
        value: lastSeen ? lastSeen - 300 : 0,
        op: "gt" as const,
      },
      type: {
        value: InvoiceType.PAYABLE,
        op: "eq" as const,
      },
    }),
    [lastSeen],
  );

  const { data: invoicesSinceLastSeen, loading: invoicesSinceLastSeenLoading } =
    useGetAllInvoices({
      query: lastSeenQuery,
    });

  const lastSeenPreviousQuery = useMemo(
    () => ({
      createdAt: {
        value: lastSeenPrevious ? lastSeenPrevious - 300 : 0,
        op: "gt" as const,
      },
      type: {
        value: InvoiceType.PAYABLE,
        op: "eq" as const,
      },
    }),
    [lastSeenPrevious],
  );

  const { data: invoicesSinceLastSeenPrevious } = useGetAllInvoices({
    query: lastSeenPreviousQuery,
  });

  useInitLastSeenValues();
  useOnAccess();

  if (
    liveCountsInvoiceProcessingLoading ||
    reconEnabledLoading ||
    liveCountsInvoiceReconLoading ||
    liveCountsTxnReconLoading
  ) {
    return null;
  }

  const hasInvoicesProcessing = liveCountsInvoiceProcessing > 0;
  const hasInvoiceRecon = reconEnabled && liveCountsInvoiceRecon > 0;
  const hasTxnReconOnly =
    reconEnabled && liveCountsInvoiceRecon === 0 && liveCountsTxnRecon > 0;

  let processingTitle: string | null = null;
  let processingCount = 0;
  let countSuffix: "invoices" | "txns" | null = null;

  if (screen === "invoices") {
    if (hasInvoicesProcessing) {
      processingTitle = t("ai_processing.belege_processing_title");
      processingCount = liveCountsInvoiceProcessing;
      countSuffix = "invoices";
    } else if (hasInvoiceRecon) {
      processingTitle = t("ai_processing.recon_processing_title");
      processingCount = liveCountsInvoiceRecon;
      countSuffix = "invoices";
    }
  } else if (screen === "txn-todos") {
    if (hasInvoiceRecon) {
      processingTitle = t("ai_processing.recon_processing_title");
      processingCount = liveCountsInvoiceRecon;
      countSuffix = "invoices";
    } else if (hasTxnReconOnly) {
      processingTitle = t("ai_processing.recon_processing_title");
      processingCount = liveCountsTxnRecon;
      countSuffix = "txns";
    }
  }

  if (processingTitle && processingCount > 0) {
    return (
      <Card className={cn("p-4 h-[80px]", className)}>
        <div className="p-0 pb-2 flex flex-row items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          <CardTitle style={{ marginTop: 0 }}>{processingTitle}</CardTitle>
          <Badge variant="outline" style={{ marginTop: 0 }}>
            {countSuffix === "invoices"
              ? t("ai_processing.processing_count", { count: processingCount })
              : t("ai_processing.processing_count_txns", {
                  count: processingCount,
                })}
          </Badge>
        </div>
        <div className="p-0">
          <p
            className="font-light text-gray-600 truncate"
            style={{ fontSize: "13px" }}
          >
            {t("ai_processing.processing_description")}
          </p>
        </div>
      </Card>
    );
  }

  if (invoicesSinceLastSeenPrevious?.length === 0) {
    return null;
  }

  return (
    <Card className={cn("p-4 flex flex-col gap-2 h-[80px]", className)}>
      <div className="flex flex-row items-center gap-2">
        <div className="w-5 h-5 rounded-full flex items-center justify-center bg-green-500">
          <Check className="w-3 h-3 text-white" />
        </div>
        <CardTitle>{t("ai_processing.processed_title")}</CardTitle>
      </div>
      <div className="flex gap-2">
        <Badge
          variant="outline"
          className="flex flex-row flex-shrink-1 items-center gap-1 font-medium"
        >
          {t("ai_processing.processed_count", {
            count: invoicesSinceLastSeenPrevious?.length,
          })}
        </Badge>
      </div>
    </Card>
  );

  function useInitLastSeenValues() {
    useEffect(() => {
      if (lastSeen === null) {
        setLastSeen(Date.now());
      }
      if (lastSeenPrevious === null) {
        setLastSeenPrevious(Date.now());
      }
    }, []);
  }

  function useOnAccess() {
    useEffect(() => {
      if (
        invoicesSinceLastSeenLoading ||
        reconEnabledLoading ||
        liveCountsInvoiceProcessing > 0
      )
        return;

      if (invoicesSinceLastSeen?.length > 0) {
        setLastSeenPrevious(lastSeen);
      }
      setLastSeen(Date.now());
    }, [
      liveCountsInvoiceProcessing,
      invoicesSinceLastSeenLoading,
      reconEnabledLoading,
    ]);
  }
}
